import { onScopeDispose } from 'vue';
import { onBeforeRouteLeave } from 'vue-router';

import { usePageReload } from '../composables/use-page-reload';

export interface UseUnsavedChangesGuardOptions {
  /**
   * Спрашивает сотрудника, можно ли потерять черновик. `false` — остаёмся.
   * Ответ может быть асинхронным: диалог приложения ждёт клика.
   */
  confirmLeave?: () => boolean | Promise<boolean>;
  hasUnsavedChanges: () => boolean;
}

export interface UseUnsavedChangesGuardReturn {
  /**
   * Одна проверка на все выходы: маршрут, мягкая перезагрузка и смена
   * контекста внутри страницы. `true` — уходить/сбрасывать можно.
   */
  confirmDiscard: () => Promise<boolean>;
}

/** Вопрос перед потерей черновика: главная кнопка оставляет редактирование. */
export const discardChangesConfirm = (message = 'Несохранённые изменения будут потеряны.') => ({
  title: 'Отменить изменения?',
  message,
  confirmText: 'Отменить изменения',
  cancelText: 'Продолжить редактирование',
  primaryAction: 'cancel' as const,
});

export function useUnsavedChangesGuard({
  confirmLeave,
  hasUnsavedChanges,
}: UseUnsavedChangesGuardOptions): UseUnsavedChangesGuardReturn {
  const { registerReloadGuard } = usePageReload();

  const beforeUnloadHandler = (e: BeforeUnloadEvent) => {
    if (hasUnsavedChanges()) {
      e.preventDefault();
    }
  };

  window.addEventListener('beforeunload', beforeUnloadHandler);

  onScopeDispose(() => {
    window.removeEventListener('beforeunload', beforeUnloadHandler);
  });

  const confirmDiscard = async (): Promise<boolean> => {
    if (!hasUnsavedChanges()) {
      return true;
    }

    return (await confirmLeave?.()) ?? confirm('Есть несохранённые изменения. Покинуть страницу?');
  };

  onBeforeRouteLeave(async () => await confirmDiscard());

  // Мягкая перезагрузка уничтожает страницу так же, как уход по маршруту:
  // спрашиваем ту же проверку, включая фискальный отказ `() => false`.
  registerReloadGuard(confirmDiscard);

  return { confirmDiscard };
}
